import React, { useEffect } from 'react';
import { Card, Row, Col, Tag, Typography, Table, Empty, Spin, Progress } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { AppDispatch, CombinedStates } from '../../core/types';
import { getIndicatorSchoolValues } from '../../core/indicator/indicatorApi';
import { IndicatorSchoolValue } from '../../core/indicator/types';
import { schoolTypeOptions } from '../../core/utils/helpers';
import Main from '../../components/layout/Main';

import './indicatorSchoolsStatus.scss';

const { Text, Title } = Typography;

const IndicatorSchoolsStatus: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [searchParams] = useSearchParams();
    const indicatorId = searchParams.get('indicator_id');
    const dateFrom = searchParams.get('date_from');

    const {
        indicatorSchoolValues,
        isFetchingIndicatorSchoolValues,
    } = useSelector((state: CombinedStates) => state.indicator);

    useEffect(() => {
        if (indicatorId && dateFrom) {
            dispatch(getIndicatorSchoolValues({
                indicator_id: Number(indicatorId),
                date: dateFrom,
            }));
        }
    }, [dispatch, indicatorId, dateFrom]);

    const toolbar = [{ label: 'Item 1', link: '/abc', iconName: 'home' }];

    if (!indicatorId) {
        return (
            <Main
                className="indicator-schools-status"
                title={<div>Rodiklio mokyklų statusas</div>}
                headerTitle={<div>Rodiklio mokyklų statusas</div>}
                menuWidth={350}
                menuPlacement="left"
                toolbar={toolbar}
                showFooter={false}
                floatingHeader={false}
                footerContent={<div />}
                floatingFooter
                backLink
                breadcrumb={null}>
                <Card style={{ marginTop: '20px' }}>
                    <Empty description="Rodiklio ID nenustatytas" />
                </Card>
            </Main>
        );
    }

    const columns = [
        {
            title: 'Mokykla',
            dataIndex: 'school_name',
            key: 'school_name',
            sorter: (a: IndicatorSchoolValue, b: IndicatorSchoolValue) => a.school_name.localeCompare(b.school_name),
        },
        {
            title: 'Tipas',
            dataIndex: 'school_type',
            key: 'school_type',
            width: 200,
            render: (type: string) => {
                const option = schoolTypeOptions.find(o => o.value === type);
                if (!option) return type || '-';
                return <Tag color={option.color}>{option.label}</Tag>;
            },
            filters: schoolTypeOptions.map(o => ({ text: o.label, value: o.value })),
            onFilter: (value: any, record: IndicatorSchoolValue) => record.school_type === value,
        },
        {
            title: 'Statusas',
            dataIndex: 'is_submitted',
            key: 'is_submitted',
            width: 120,
            render: (isSubmitted: boolean) => (
                isSubmitted
                    ? <Tag icon={<CheckCircleOutlined />} color="success">Užpildyta</Tag>
                    : <Tag icon={<CloseCircleOutlined />} color="error">Nepateikta</Tag>
            ),
            filters: [
                { text: 'Užpildyta', value: true },
                { text: 'Nepateikta', value: false },
            ],
            onFilter: (value: any, record: IndicatorSchoolValue) => record.is_submitted === value,
        },
        {
            title: 'Reikšmė',
            dataIndex: 'value',
            key: 'value',
            width: 100,
            render: (value: number | null, record: IndicatorSchoolValue) => {
                if (value !== null && value !== undefined) return value;
                if (record.value_text) return record.value_text;
                return '-';
            },
        },
        {
            title: 'Pateikimo data',
            dataIndex: 'submitted_at',
            key: 'submitted_at',
            width: 160,
            render: (date: string | null) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-'),
            sorter: (a: IndicatorSchoolValue, b: IndicatorSchoolValue) => {
                if (!a.submitted_at && !b.submitted_at) return 0;
                if (!a.submitted_at) return 1;
                if (!b.submitted_at) return -1;
                return dayjs(a.submitted_at).unix() - dayjs(b.submitted_at).unix();
            },
        },
        {
            title: 'Patvirtinimo data',
            dataIndex: 'confirmed_at',
            key: 'confirmed_at',
            width: 160,
            render: (date: string | null) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-'),
        },
    ];

    const indicator = indicatorSchoolValues?.indicator;
    const statistics = indicatorSchoolValues?.statistics;
    const pageTitle = indicator
        ? `${indicator.title} (R${indicator.id}) — mokyklų statusas`
        : `Rodiklio R${indicatorId} mokyklų statusas`;

    return (
        <Main
            className="indicator-schools-status"
            title={<div>{pageTitle}</div>}
            headerTitle={<div>{pageTitle}</div>}
            menuWidth={350}
            menuPlacement="left"
            toolbar={toolbar}
            showFooter={false}
            floatingHeader={false}
            footerContent={<div />}
            floatingFooter
            backLink
            breadcrumb={null}>
            <Card style={{ marginTop: '20px' }}>
                <Spin spinning={isFetchingIndicatorSchoolValues}>
                    {indicatorSchoolValues ? (
                        <>
                            <div className="status-info">
                                <Row gutter={[16, 16]}>
                                    <Col span={24}>
                                        <Title level={4}>{indicator?.title} (R{indicator?.id})</Title>
                                        {indicator?.unit && (
                                            <Text type="secondary">Vienetas: {indicator.unit}</Text>
                                        )}
                                    </Col>
                                    <Col span={24}>
                                        <Text>
                                            <Text strong>Laikotarpis: </Text>
                                            {indicatorSchoolValues.period_description || indicatorSchoolValues.period}
                                        </Text>
                                    </Col>
                                </Row>

                                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                                    <Col xs={12} sm={6}>
                                        <Card size="small">
                                            <Text type="secondary">Iš viso mokyklų</Text>
                                            <Title level={3} style={{ margin: 0 }}>
                                                {statistics?.total_schools}
                                            </Title>
                                        </Card>
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <Card size="small">
                                            <Text type="secondary">Užpildė</Text>
                                            <Title level={3} style={{ margin: 0, color: '#52c41a' }}>
                                                {statistics?.submitted}
                                            </Title>
                                        </Card>
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <Card size="small">
                                            <Text type="secondary">Nepateikė</Text>
                                            <Title level={3} style={{ margin: 0, color: '#ff4d4f' }}>
                                                {statistics?.not_submitted}
                                            </Title>
                                        </Card>
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <Card size="small">
                                            <Text type="secondary">Užpildymas</Text>
                                            <Progress
                                                percent={statistics?.completion_rate ?? 0}
                                                size="small"
                                                status={(() => {
                                                    const rate = statistics?.completion_rate ?? 0;
                                                    if (rate >= 80) return 'success';
                                                    if (rate >= 50) return 'active';
                                                    return 'exception';
                                                })()} />
                                        </Card>
                                    </Col>
                                </Row>
                            </div>

                            <Card title="Mokyklų sąrašas" className="schools-table-card" style={{ marginTop: 24 }}>
                                <Table
                                    dataSource={indicatorSchoolValues.schools}
                                    columns={columns}
                                    rowKey="school_id"
                                    pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (total) => `Iš viso: ${total}` }}
                                    size="small" />
                            </Card>
                        </>
                    ) : (
                        !isFetchingIndicatorSchoolValues && (
                            <Empty description="Duomenų nėra" />
                        )
                    )}
                </Spin>
            </Card>
        </Main>
    );
};

export default IndicatorSchoolsStatus;
